import React, { useEffect, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/Select";

interface S3JsonDropdownProps {
  bucketName: string;
  jsonKey: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}

export const S3JsonDropdown: React.FC<S3JsonDropdownProps> = ({
  bucketName,
  jsonKey,
  value,
  onChange,
  label,
  placeholder = "Select an option",
  className
}) => {
  const [options, setOptions] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchJsonData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Construct the S3 URL
        const s3Url = `https://${bucketName}.s3.amazonaws.com/${jsonKey}`;
        
        // Fetch the JSON data
        const response = await fetch(s3Url);
        if (!response.ok) {
          throw new Error(`Failed to fetch JSON data: ${response.statusText}`);
        }
        
        const data = await response.json();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load options');
        console.error('Error fetching JSON data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJsonData();
  }, [bucketName, jsonKey]);

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        <span className="text-sm text-gray-500">Loading LLM models...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-500">
        Error loading LLM models: {error}
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(options).map(([key, value]) => (
            <SelectItem key={key} value={value}>
              {key}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}; 